import React from 'react'
import { Link } from 'react-router-dom'
import { Icon, Tooltip } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useSectionNav } from 'app/hooks/useSectionNav'
import { convertHexToRGB } from 'utils'

const useStyles = makeStyles(({ palette, ...theme }) => ({
  rail: ({ primaryRGB }) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: 'var(--sidenav-compact-width)',
    boxShadow: theme.shadows[8],
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top',
    backgroundSize: 'cover',
    zIndex: 111,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px 0',
    backgroundImage: `linear-gradient(to bottom, rgba(${primaryRGB}, 0.96), rgba(${primaryRGB}, 0.96)), url(${theme.loginBg})`,
  }),
  logo: {
    width: '2rem',
    marginBottom: 24,
    flexShrink: 0,
  },
  item: {
    width: 44,
    height: 44,
    borderRadius: 11,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    flexShrink: 0,
    color: palette.primary.contrastText,
    opacity: 0.75,
    transition: 'background-color 150ms ease-in, opacity 150ms ease-in',
    '&:hover': {
      opacity: 1,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  itemActive: {
    opacity: 1,
    backgroundColor: palette.secondary.main,
    color: palette.secondary.contrastText,
  },
}))

// The 80px icon rail that replaced the old ~20-item list (`Sidenav` +
// `MatxVerticalNav`, still in the tree but no longer rendered by
// `Layout1Sidenav`). One icon per major `sections` entry; clicking one
// navigates to its first tab. Which section reads as active — and
// which tabs show up next to it — comes from `useSectionNav`, and
// `SectionTopBar` renders the rest of the redesign (title, tabs, page
// actions) alongside this.
const SectionRail = () => {
  const theme = useTheme()
  const { sections, activeSection } = useSectionNav()
  const classes = useStyles({
    primaryRGB: convertHexToRGB(theme.palette.primaryDark.main),
  })

  return (
    <nav className={classes.rail} data-testid="section-rail">
      {!!theme.logoSymbol && (
        <img src={theme.logoSymbol} className={classes.logo} alt="" />
      )}

      {sections.map((section) => {
        const target = section.children.find(
          (child) => child.type !== 'extLink'
        )
        if (!target) return null

        return (
          <Tooltip key={section.id} title={section.name} placement="right">
            <Link
              to={target.path}
              className={clsx(classes.item, {
                [classes.itemActive]: activeSection?.id === section.id,
              })}
              aria-label={section.name}
            >
              <Icon>{section.icon}</Icon>
            </Link>
          </Tooltip>
        )
      })}
    </nav>
  )
}

export default SectionRail
